import { Box, Button, ButtonRow, FormSection, Heading, KsyosLogo, Link, Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import { serviceDeskEmailAddress } from '../../../../common/constants';
import { TwoFactorAuthenticationType } from '../../../../common/enums/twoFactorAuthenticationType';
import { addTrackEvent } from '../customControls/utils/formAnalytics';
import { StyledContainer, StyledForm } from '../notLoggedIn/LoginStyledComponents';
import { MfaAppsInfo } from '../notLoggedIn/MfaAppsInfo';
import { MfaCodeInput } from '../notLoggedIn/MfaCodeInput';
import { MfaQrCodeForm } from '../notLoggedIn/MfaQrCodeForm';
import { MfaSuccessScreen } from '../notLoggedIn/MfaSuccessScreen';

export function SetMfa(props: { location?: { search?: string } }) {
    const navigate = useNavigate();
    const translate = getSelectedLanguage();
    const [step, setStep] = useState<'info' | 'skipped' | 'mfaAppsInfo' | 'qrCode' | 'inputCode' | 'success'>('info');
    const [otpSecret, setOtpSecret] = useState<string>();

    useEffect(() => {
        if (step === 'success') {
            setTimeout(() => navigate(getRedirectUrlFromLocation()), 2000);
        }
    }, [step]);

    return (
        <>
            <StyledContainer className="mt-5">
                <StyledForm submitOnEnter={false}>
                    <FormSection isBorderless>
                        <Box
                            mb={4}
                            textAlign="center"
                        >
                            <KsyosLogo />
                        </Box>
                        <FormSection isBorderless>
                            <Heading
                                size="xlarge"
                                textAlign="center"
                            >
                                Tweestapsverificatie instellen
                            </Heading>
                        </FormSection>

                        {step === 'info' && (
                            <>
                                <FormSection isBorderless>
                                    <Heading
                                        size="large"
                                        textAlign="center"
                                    >
                                        Tweestapsverificatie wordt verplicht{' '}
                                    </Heading>
                                </FormSection>

                                <FormSection isBorderless>
                                    <Text textAlign="center">
                                        Stel nu tweestapsverificatie in en voorkom dat je vanaf 1 november niet meer
                                        kunt inloggen.
                                    </Text>
                                </FormSection>

                                <FormSection isBorderless>
                                    <img
                                        alt="mfa"
                                        className={'rounded mx-auto d-block'}
                                        src="/assets/images/mfa.svg"
                                        style={{ maxWidth: '205px' }}
                                    />
                                </FormSection>

                                <FormSection isBorderless>
                                    <Text textAlign="center">
                                        Het instellen is zo gedaan.
                                        <br />
                                        Het enige dat je nodig hebt is een <b>smartphone.</b>
                                    </Text>
                                </FormSection>

                                <FormSection
                                    isBorderless
                                    style={{
                                        marginTop: '32px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Link
                                        color="minimal"
                                        fontWeight="normal"
                                        href="https://www.ksyos.nl/hardware-token-voor-2fa-bestellen/"
                                        onClick={() =>
                                            addTrackEvent(
                                                'SetMfa-info',
                                                'Link Click',
                                                'Ik kan geen smartphone gebruiken'
                                            )
                                        }
                                        openInNewTab
                                        variant="externalLink"
                                    >
                                        Ik kan geen smartphone gebruiken
                                    </Link>
                                </FormSection>
                            </>
                        )}
                        {step === 'skipped' && (
                            <>
                                <FormSection isBorderless>
                                    <Heading
                                        size="large"
                                        textAlign="center"
                                    >
                                        Voorkom dat je niet meer kunt inloggen
                                    </Heading>
                                </FormSection>

                                <FormSection isBorderless>
                                    <Text textAlign="center">
                                        Tweestapsverificatie is vanaf 1 november verplicht. Stel het op tijd in, anders
                                        is inloggen niet meer mogelijk.
                                    </Text>
                                </FormSection>

                                <FormSection isBorderless>
                                    <img
                                        alt="mfa"
                                        className={'rounded mx-auto d-block'}
                                        src="/assets/images/no-access-2FA.svg"
                                        style={{ maxWidth: '490px' }}
                                    />
                                </FormSection>
                            </>
                        )}

                        {step === 'mfaAppsInfo' && <MfaAppsInfo />}
                        {step === 'qrCode' && <MfaQrCodeForm onOtpSecretChange={(newOtp) => setOtpSecret(newOtp)} />}
                        {step === 'inputCode' && otpSecret && (
                            <MfaCodeInput
                                onCodeVerified={onMfaVerified}
                                otpSecret={otpSecret}
                            />
                        )}
                        {step === 'success' && (
                            <MfaSuccessScreen mfaType={TwoFactorAuthenticationType.verificationApp} />
                        )}

                        {getButtons()}
                    </FormSection>
                </StyledForm>

                <Box mb={8}>
                    <Text
                        color="minimal"
                        mb={1}
                        size="sm"
                        textAlign="center"
                    >
                        Hulp nodig bij het instellen?
                    </Text>

                    <Text
                        color="minimal"
                        mb={1}
                        size="sm"
                        textAlign="center"
                    >
                        Bekijk de{' '}
                        <Link
                            color="minimal"
                            href={'https://vimeo.com/636933672/383bea2592'}
                            openInNewTab
                            variant="externalLink"
                        >
                            instructievideo over tweestapsverificatie
                        </Link>
                        .
                    </Text>

                    <Text
                        color="minimal"
                        size="sm"
                        textAlign="center"
                    >
                        {translateKey('login.signInHelpCall')}{' '}
                        <Text
                            as="span"
                            color="minimal"
                            fontWeight="bold"
                            size="sm"
                        >
                            020 600 00 60{' '}
                        </Text>
                        {translateKey('login.signInHelpOrMail')}{' '}
                        <Link
                            color="minimal"
                            href={`mailto:${serviceDeskEmailAddress}`}
                            variant="externalLink"
                        >
                            {serviceDeskEmailAddress}
                        </Link>
                        <br />
                    </Text>
                </Box>
            </StyledContainer>
        </>
    );

    function getButtons() {
        let primaryOnClick: (() => void) | undefined = undefined;
        let primaryLabel: string = '';
        let secondaryOnClick: (() => void) | undefined = undefined;
        let secondaryLabel: string = '';

        switch (step) {
            case 'info':
                primaryOnClick = () => setStep('mfaAppsInfo');
                primaryLabel = translate.common.next;
                secondaryOnClick = () => setStep('skipped');
                secondaryLabel = 'Overslaan';
                break;
            case 'skipped':
                primaryOnClick = () => {
                    addTrackEvent('SetMfa-skipped', 'Button Click', 'Stel dit later in, door naar Ksyos');
                    navigate(getRedirectUrlFromLocation());
                };
                primaryLabel = 'Stel dit later in, door naar Ksyos';
                secondaryOnClick = () => setStep('mfaAppsInfo');
                secondaryLabel = 'Tweestapsverificatie instellen';
                break;
            case 'mfaAppsInfo':
                primaryOnClick = () => setStep('qrCode');
                primaryLabel = translate.common.next;
                secondaryOnClick = () => setStep('info');
                secondaryLabel = translate.common.previous;
                break;
            case 'qrCode':
                primaryOnClick = () => setStep('inputCode');
                primaryLabel = translate.common.next;
                secondaryOnClick = () => setStep('mfaAppsInfo');
                secondaryLabel = translate.common.previous;
                break;
            case 'inputCode':
                secondaryOnClick = () => setStep('qrCode');
                secondaryLabel = translate.common.previous;
                break;
            default:
                break;
        }

        return (
            <FormSection isBorderless>
                <ButtonRow justify="center">
                    {primaryOnClick && (
                        <Button
                            color="secondary"
                            key="next"
                            onClick={primaryOnClick}
                            width="full"
                        >
                            {primaryLabel}
                        </Button>
                    )}
                    {secondaryOnClick && (
                        <Button
                            color="secondary"
                            key="prev"
                            onClick={secondaryOnClick}
                            variant="link"
                            width="full"
                        >
                            {secondaryLabel}
                        </Button>
                    )}
                </ButtonRow>
            </FormSection>
        );
    }

    function onMfaVerified() {
        addTrackEvent('SetMfa-onMfaVerified', 'onMfaVerified', 'Ga door naar succespagina');
        setTimeout(() => setStep('success'), 1000);
    }

    function getRedirectUrlFromLocation() {
        let redirectTo: string | undefined;
        if (props.location?.search?.startsWith('?redirectTo=/')) {
            redirectTo = props.location.search.substr(12);
        }

        return redirectTo ? redirectTo : '/home';
    }
}
